import axiosInstance from '@/api/axiosInstance';
import { buildQueryString } from '@/utils/query';
import { ExportFormat } from '@/constants/export-dropdown.constants';
import {
  Position, PositionCreate, PositionUpdate, PositionFilterParams, PaginatedPositionsResponse,
  PositionCfn, PositionCfnCreateDto, PositionCfnUpdateDto, PaginatedPositionCfnsResponse,
} from '@/types/position.types';
import { POSITION_ENDPOINTS } from '@/api/endpoints/position.endpoints';
import { ManagementApiService, FormApiService } from '@/types/management.types';

export const PositionManagementApiService: ManagementApiService<Position, PositionFilterParams> = {
  getAll: async (offset = 0, limit = 20, filters?: PositionFilterParams): Promise<PaginatedPositionsResponse> => {
    const query = buildQueryString({ ...filters, offset, limit });
    return (await axiosInstance.get<PaginatedPositionsResponse>(`${POSITION_ENDPOINTS.POSITIONS}${query}`)).data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(POSITION_ENDPOINTS.POSITION_DETAIL(id));
  },
  exportItems: async (format: ExportFormat, filters?: Omit<PositionFilterParams, 'offset' | 'limit'>): Promise<Blob> => {
    const query = buildQueryString({ ...filters, format: format.toLowerCase() });
    return (await axiosInstance.get<Blob>(`${POSITION_ENDPOINTS.POSITIONS_EXPORT}${query}`, { responseType: 'blob' })).data;
  },
};

export const PositionFormApiService: FormApiService<Position, PositionCreate, PositionUpdate> & {
  createItemForTenant: (data: PositionCreate, tenantId?: number | null) => Promise<Position>;
} = {
  // Edit-form fetch: keep multilang tags so the per-language title editor
  // round-trips every translation (skipMultilangStrip).
  getItem: async (id: number | string): Promise<Position> =>
    (await axiosInstance.get<Position>(
      POSITION_ENDPOINTS.POSITION_DETAIL(Number(id)),
      { skipMultilangStrip: true } as object,
    )).data,
  createItem: async (data: PositionCreate): Promise<Position> =>
    (await axiosInstance.post<Position>(POSITION_ENDPOINTS.POSITIONS, data)).data,
  createItemForTenant: async (data: PositionCreate, tenantId?: number | null): Promise<Position> => {
    const query = tenantId ? `?tenant_id=${tenantId}` : '';
    return (await axiosInstance.post<Position>(`${POSITION_ENDPOINTS.POSITIONS}${query}`, data)).data;
  },
  updateItem: async (id: number, data: PositionUpdate): Promise<Position> =>
    (await axiosInstance.put<Position>(POSITION_ENDPOINTS.POSITION_DETAIL(id), data)).data,
};


export const PositionCfnManagementApiService: ManagementApiService<PositionCfn, { name?: string }> = {
  getAll: async (offset = 0, limit = 10, filters?: { name?: string }): Promise<PaginatedPositionCfnsResponse> => {
    const query = buildQueryString({ ...filters, offset, limit });
    return (await axiosInstance.get<PaginatedPositionCfnsResponse>(`${POSITION_ENDPOINTS.POSITIONS_CUSTOM_FIELDS}${query}`)).data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(POSITION_ENDPOINTS.POSITION_CUSTOM_FIELD_DETAIL(id));
  },
};

const parseConfig = (config: unknown): object | null => {
  if (typeof config === 'string') {
    try { return JSON.parse(config); } catch { return config === '{}' ? {} : null; }
  }
  if (typeof config === 'object') return config as object;
  return null;
};

export const PositionCfnFormApiService: FormApiService<PositionCfn, PositionCfnCreateDto, PositionCfnUpdateDto> = {
  createItem: async (data: PositionCfnCreateDto): Promise<PositionCfn> =>
    (await axiosInstance.post<PositionCfn>(POSITION_ENDPOINTS.POSITIONS_CUSTOM_FIELDS, { ...data, config: parseConfig(data.config) })).data,
  updateItem: async (id: number, data: PositionCfnUpdateDto): Promise<PositionCfn> =>
    (await axiosInstance.put<PositionCfn>(POSITION_ENDPOINTS.POSITION_CUSTOM_FIELD_DETAIL(id), { ...data, config: parseConfig(data.config) })).data,
};

export const PositionsApi = {
  Management: PositionManagementApiService,
  Form: PositionFormApiService,
  CfnManagement: PositionCfnManagementApiService,
  CfnForm: PositionCfnFormApiService,
  // backward compatibility
  getPositions: PositionManagementApiService.getAll,
  getPositionById: PositionFormApiService.getItem!,
  createPosition: PositionFormApiService.createItem,
  updatePosition: PositionFormApiService.updateItem,
  deletePosition: PositionManagementApiService.deleteItem,
  exportPositions: PositionManagementApiService.exportItems,
  getPositionCustomFieldDefinitions: async (params?: { limit?: number }): Promise<PaginatedPositionCfnsResponse> => {
    const query = buildQueryString(params || {});
    return (await axiosInstance.get<PaginatedPositionCfnsResponse>(`${POSITION_ENDPOINTS.POSITIONS_CUSTOM_FIELDS}${query}`)).data;
  },
};